import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Share } from '@capacitor/share';
import { Box, Button, Flex, Text, VStack } from '@chakra-ui/react';
import imageCompression from 'browser-image-compression';
import { supabase } from '@/lib/supabase';
import type { ChatMention, ChatMessage, ChatThreadProps } from './types';
import { mergeMessages, setThreadCache } from './cache/messageCache';
import { MESSAGE_SELECT_FIELDS } from './messageSelectFields';
import { useChatMessages } from './hooks/useChatMessages';
import { useChatMemberIds } from './hooks/useChatMemberIds';
import { useMessageReads } from './hooks/useMessageReads';
import { useSignedUrls } from './hooks/useSignedUrls';
import { useChatScroll } from './hooks/useChatScroll';
import { useThreadMentionCandidates } from './hooks/useThreadMentionCandidates';
import { useMentionAutocomplete } from './hooks/useMentionAutocomplete';
import { trimMentions } from './mentions/reconcileMentions';
import { isMessageSeenByAllMembers } from './utils/isMessageSeenByAllMembers';
import { MessageBubble } from './components/MessageBubble';
import { ChatInput } from './components/ChatInput';
import { MentionSuggestions } from './components/MentionSuggestions';
import { ImageConfirmDialog } from './components/ImageConfirmDialog';
import { ImageLightbox } from './components/ImageLightbox';
import { validateChatMessage } from '@/utils/chatProfanity';
import { setActiveChatKey } from '@/utils/activeChatKey';
import { chatKey, storagePrefix, threadInsertKey } from './chatTarget';

export type { ChatMessage, ChatTarget, ChatThreadProps, MessageRead, User } from './types';

const formatFileSize = (bytes: number | null | undefined) => {
  if (bytes == null) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

/**
 * One chat thread: either an event's group chat or a private 1-on-1 thread.
 * Always renders list + input only; the surrounding chrome belongs to the page.
 */
export function ChatThread({
  target,
  directParticipantIds,
  onMessageSent,
  canPin = false,
  isMessagePinned,
  onPinMessage,
  onUnpinMessage,
  focusMessage,
  onMessageFocused,
  profileReturnTo,
}: ChatThreadProps) {
  const navigate = useNavigate();

  // --- hooks ---
  const { messages, setMessages, loading, hasMore, setHasMore, currentUserId, currentUserIdRef } =
    useChatMessages(target);

  const key = chatKey(target);

  const appendInsertedMessage = (row: ChatMessage) => {
    setMessages((prev) => {
      const next = mergeMessages(prev, [row]);
      void setThreadCache(key, { messages: next, hasMore });
      return next;
    });
  };

  // Presence: while this thread is on screen the fan-out trigger must not notify anyone
  // about it -- the message is already in front of them. Cleared on unmount so leaving
  // the thread resumes notifications immediately rather than after the TTL.
  useEffect(() => {
    setActiveChatKey(key);
    return () => setActiveChatKey(null);
  }, [key]);

  const { readsByMessageId, markAllAsRead, refetchReads } = useMessageReads(target, messages, currentUserId);
  const { memberIds } = useChatMemberIds(target, directParticipantIds);
  const { signedUrls, pdfSignedUrls } = useSignedUrls(target, messages);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { loadingMore, loadOlderMessages, scrollToBottom, revealMessage } = useChatScroll({
    target,
    scrollContainerRef,
    messages,
    setMessages,
    loading,
    signedUrls,
    setHasMore,
  });

  // --- local state (attachments / dialogs) ---
  const [newMessage, setNewMessage] = useState('');
  /** Who the draft names, kept in step with newMessage by reconcileMentions. */
  const [mentions, setMentions] = useState<ChatMention[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The message a pin-list tap jumped to, flashed briefly so the reader can see it. */
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [optimisticPdfMessage, setOptimisticPdfMessage] = useState<{
    fileName: string;
    fileSize: number;
  } | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [pendingImage, setPendingImage] = useState<{
    blob: Blob;
    path: string;
    contentType: string;
  } | null>(null);
  const [pendingImagePreviewUrl, setPendingImagePreviewUrl] = useState<string | null>(null);
  const [imageConfirmOpen, setImageConfirmOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const documentInputRef = useRef<HTMLInputElement | null>(null);
  const prevMsgCountRef = useRef(0);

  useEffect(() => {
    setError(null);
    prevMsgCountRef.current = 0;
  }, [key]);

  // Bridge: fetch initial reads + mark-as-read once messages are loaded and viewer id is known.
  // With IndexedDB cache, loading becomes false before the auth context resolves the viewer id;
  // deps must include currentUserId or refetchReads never runs and “seen” stays empty until a
  // full page refresh.
  useEffect(() => {
    if (loading || !messages.length) return;
    const uid = currentUserIdRef.current;
    if (!uid) return;
    markAllAsRead();
    refetchReads(messages.map((m) => m.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- markAllAsRead/refetchReads not stable refs
  }, [loading, currentUserId, messages.length]);

  // Bridge: mark-as-read when a real-time message from another user arrives
  useEffect(() => {
    if (loading || !messages.length) return;
    // Skip the initial load — bridge #1 already handles that
    if (prevMsgCountRef.current === 0) {
      prevMsgCountRef.current = messages.length;
      return;
    }
    prevMsgCountRef.current = messages.length;
    const last = messages[messages.length - 1];
    const uid = currentUserIdRef.current;
    if (uid && last.user_id !== uid) {
      markAllAsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  // Jump to a message someone tapped in the pinned list. revealMessage pages backwards
  // until it finds it, so a pin far up the history still works.
  useEffect(() => {
    if (!focusMessage) return;
    let cancelled = false;

    void (async () => {
      const found = await revealMessage(focusMessage.id, focusMessage.createdAt);
      if (cancelled) return;
      if (found) {
        setHighlightedMessageId(focusMessage.id);
        window.setTimeout(() => setHighlightedMessageId(null), 2000);
      } else {
        // Reuses the composer's error line rather than the list pane's: on a phone the
        // list is not even on screen while a chat is open. Typing clears it.
        setError('Could not find that message — it may be too far back in the chat.');
      }
      onMessageFocused?.(found);
    })();

    return () => {
      cancelled = true;
    };
    // revealMessage is redefined every render; keying on the request is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMessage?.id, focusMessage?.createdAt]);

  // --- @mentions ---
  const { candidates: mentionCandidates } = useThreadMentionCandidates(
    target,
    memberIds,
    currentUserId,
  );
  const mentionPicker = useMentionAutocomplete({
    value: newMessage,
    mentions,
    candidates: mentionCandidates,
    onChange: (text, nextMentions) => {
      if (error) setError(null);
      setNewMessage(text);
      setMentions(nextMentions);
    },
  });

  // --- handlers ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Offsets are recorded against the draft, but the INSERT sends the trimmed string --
    // leading whitespace would slide every chip one character left, which reads as a font
    // bug rather than an offset bug. trimMentions re-bases them and drops any that no
    // longer read `@Name`.
    const { text: trimmed, mentions: sentMentions } = trimMentions(newMessage, mentions);
    if (!trimmed || sending) return;

    const validationError = validateChatMessage(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!currentUserId) {
      setError('You must be logged in to send messages.');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const { data: inserted, error: insertError } = await supabase
        .from('messages_new')
        .insert({
          ...threadInsertKey(target),
          content: trimmed,
          user_id: currentUserId,
          mentions: sentMentions,
        })
        .select(MESSAGE_SELECT_FIELDS)
        .single();

      if (insertError) {
        setError(insertError.message || 'Failed to send message.');
        return;
      }
      if (!inserted) {
        setError('Failed to send message.');
        return;
      }
      appendInsertedMessage(inserted as ChatMessage);
      setNewMessage('');
      setMentions([]);
      mentionPicker.reset();
      onMessageSent?.(inserted as ChatMessage);
    } finally {
      setSending(false);
    }
  };

  const handleMessageChange = (value: string, caret: number) => {
    mentionPicker.handleTextChange(value, caret);
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setError(null);

    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      });

      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
      const fileId = crypto.randomUUID();
      const storagePath = `${storagePrefix(target)}/${fileId}.${ext}`;
      const previewUrl = URL.createObjectURL(compressed);

      setPendingImage({ blob: compressed, path: storagePath, contentType: file.type });
      setPendingImagePreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return previewUrl;
      });
      setImageConfirmOpen(true);
    } catch (err) {
      console.error('[ChatThread] Image compression failed', err);
      setError('Failed to prepare image.');
    }
  };

  const handleCancelPendingImage = () => {
    if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
    setPendingImage(null);
    setPendingImagePreviewUrl(null);
    setImageConfirmOpen(false);
  };

  const handleConfirmSendImage = async () => {
    if (!pendingImage) return;
    if (!currentUserId) {
      setError('You must be logged in to send images.');
      return;
    }
    setUploadingImage(true);
    setError(null);

    try {
      const { error: uploadError } = await supabase.storage
        .from('chat-images')
        .upload(pendingImage.path, pendingImage.blob, {
          contentType: pendingImage.contentType,
          upsert: false,
        });

      if (uploadError) {
        setError('Failed to upload image.');
        return;
      }

      const { data: inserted, error: insertError } = await supabase
        .from('messages_new')
        .insert({
          ...threadInsertKey(target),
          content: '',
          user_id: currentUserId,
          image_url: pendingImage.path,
        })
        .select(MESSAGE_SELECT_FIELDS)
        .single();

      if (insertError) {
        await supabase.storage.from('chat-images').remove([pendingImage.path]);
        setError(insertError.message || 'Failed to send image.');
        return;
      }
      if (!inserted) {
        await supabase.storage.from('chat-images').remove([pendingImage.path]);
        setError('Failed to send image.');
        return;
      }

      appendInsertedMessage(inserted as ChatMessage);
      onMessageSent?.(inserted as ChatMessage);
      handleCancelPendingImage();
    } catch (err) {
      console.error('[ChatThread] Image upload failed', err);
      setError('Failed to send image.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDocumentSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (file.type !== 'application/pdf') {
      setError('Only PDF files are supported.');
      return;
    }
    const MAX_PDF_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_PDF_SIZE) {
      setError('PDF must be smaller than 5 MB.');
      return;
    }

    if (!currentUserId) {
      setError('You must be logged in to send files.');
      return;
    }

    setError(null);
    setOptimisticPdfMessage({ fileName: file.name, fileSize: file.size });
    setUploadingPdf(true);

    const fileId = crypto.randomUUID();
    const storagePath = `${storagePrefix(target)}/${fileId}.pdf`;

    try {
      const { error: uploadError } = await supabase.storage
        .from('chat-documents')
        .upload(storagePath, file, { contentType: 'application/pdf', upsert: false });

      if (uploadError) {
        setError('Failed to upload PDF.');
        return;
      }

      const { data: inserted, error: insertError } = await supabase
        .from('messages_new')
        .insert({
          ...threadInsertKey(target),
          content: '',
          user_id: currentUserId,
          type: 'pdf',
          file_url: storagePath,
          file_name: file.name,
          file_size: file.size,
        })
        .select(MESSAGE_SELECT_FIELDS)
        .single();

      if (insertError) {
        await supabase.storage.from('chat-documents').remove([storagePath]);
        setError(insertError.message || 'Failed to send PDF.');
        return;
      }
      if (!inserted) {
        await supabase.storage.from('chat-documents').remove([storagePath]);
        setError('Failed to send PDF.');
        return;
      }

      appendInsertedMessage(inserted as ChatMessage);
      onMessageSent?.(inserted as ChatMessage);
    } catch (err) {
      console.error('[ChatThread] PDF upload failed', err);
      setError('Failed to send PDF.');
    } finally {
      setUploadingPdf(false);
      setOptimisticPdfMessage(null);
    }
  };

  const handleDownloadLightboxImage = async () => {
    if (!lightboxUrl) return;
    try {
      await Share.share({ url: lightboxUrl, title: 'volyx-chat-photo', dialogTitle: 'Save photo' });
    } catch {
      // User cancelled the share sheet — not an error
    }
  };

  // --- render pieces ---
  const messageList = (
    <Box
      ref={scrollContainerRef}
      flex={1}
      overflowY="auto"
      minH={0}
      py={5}
      px={5}
      bg="white"
      display="flex"
      flexDirection="column"
      gap={4}
    >
      {loading ? (
        <Flex justify="center" py={8}>
          <Text fontSize="sm" color="gray.500">
            Loading messages…
          </Text>
        </Flex>
      ) : messages.length === 0 ? (
        <VStack flex={1} justify="center" py={12} gap={1}>
          <Text fontSize="sm" color="gray.500">
            No messages yet
          </Text>
          <Text fontSize="xs" color="gray.400">
            Start the conversation below
          </Text>
        </VStack>
      ) : (
        <VStack align="stretch" gap="10px">
          {hasMore && (
            <Flex justify="center">
              <Button
                type="button"
                size="xs"
                variant="outline"
                colorPalette="gray"
                disabled={loadingMore}
                onClick={loadOlderMessages}
              >
                {loadingMore ? 'Loading…' : 'See older messages'}
              </Button>
            </Flex>
          )}

          {messages.map((message, index) => {
            const isOwn = currentUserId != null && message.user_id === currentUserId;
            const messageReads = readsByMessageId[message.id] ?? [];
            const readers = messageReads.filter((r) => r.user_id !== message.user_id);
            const isSeen =
              message.user_id != null &&
              isMessageSeenByAllMembers(message.user_id, messageReads, memberIds);
            const timestamp = new Date(message.created_at).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            });

            const prev = messages[index - 1];
            const next = messages[index + 1];
            const isGroupFirst = !prev || prev.user_id !== message.user_id;
            const isGroupLast = !next || next.user_id !== message.user_id;

            return (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={isOwn}
                isSeen={isSeen}
                timestamp={timestamp}
                isGroupFirst={isGroupFirst}
                isGroupLast={isGroupLast}
                signedImageUrl={message.image_url ? signedUrls[message.id] : undefined}
                pdfUrl={pdfSignedUrls[message.id]}
                readers={readers}
                formatFileSize={formatFileSize}
                onOpenLightbox={(url) => setLightboxUrl(url)}
                onScrollToBottomInstant={() => scrollToBottom('instant')}
                onOpenProfile={(userId) => {
                  // The return URL travels in history state rather than the query string
                  // so it stays out of the link the profile's share button copies.
                  if (userId) navigate(`/profile?id=${userId}`, { state: { returnTo: profileReturnTo } });
                }}
                canPin={canPin}
                isHighlighted={highlightedMessageId === message.id}
                isPinned={isMessagePinned?.(message.id) ?? false}
                onPin={onPinMessage}
                onUnpin={onUnpinMessage}
              />
            );
          })}

          {optimisticPdfMessage && (
            <Flex maxW="100%" alignSelf="flex-end" alignItems="flex-end" gap={1.5}>
              <Box
                px={2.5}
                py={1.5}
                borderRadius="12px"
                bg="blue.500"
                color="white"
                opacity={0.7}
                display="flex"
                alignItems="center"
                gap={2.5}
                minW="180px"
                maxW="260px"
              >
                <Box flexShrink={0} color="white">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z" />
                  </svg>
                </Box>
                <Box minW={0} flex="1">
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="white"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    display="block"
                  >
                    {optimisticPdfMessage.fileName}
                  </Text>
                  <Text fontSize="10px" color="blue.100">
                    {formatFileSize(optimisticPdfMessage.fileSize)}
                  </Text>
                </Box>
                <Box flexShrink={0}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.3" />
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 12 12"
                        to="360 12 12"
                        dur="0.8s"
                        repeatCount="indefinite"
                      />
                    </path>
                  </svg>
                </Box>
              </Box>
            </Flex>
          )}
          <Box />
        </VStack>
      )}
    </Box>
  );

  return (
    <>
      <Flex flexDirection="column" w="full" flex={1} minH={0} overflow="hidden">
        {messageList}
        <ChatInput
          newMessage={newMessage}
          setNewMessage={handleMessageChange}
          onSubmit={handleSubmit}
          sending={sending}
          error={error}
          uploadingImage={uploadingImage}
          uploadingPdf={uploadingPdf}
          fileInputRef={fileInputRef}
          documentInputRef={documentInputRef}
          onImageSelect={handleImageSelect}
          onDocumentSelect={handleDocumentSelect}
          messageInputRef={mentionPicker.inputRef}
          onMessageKeyDown={mentionPicker.handleKeyDown}
          onCaretChange={mentionPicker.handleCaretChange}
          mentionSuggestions={
            <MentionSuggestions
              suggestions={mentionPicker.suggestions}
              activeIndex={mentionPicker.activeIndex}
              onHighlight={mentionPicker.setActiveIndex}
              onSelect={mentionPicker.select}
            />
          }
        />
      </Flex>
      <ImageConfirmDialog
        open={imageConfirmOpen}
        previewUrl={pendingImagePreviewUrl}
        uploading={uploadingImage}
        onConfirm={handleConfirmSendImage}
        onCancel={handleCancelPendingImage}
      />
      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} onDownload={handleDownloadLightboxImage} />
    </>
  );
}
